/*
 * The answer-language picker's data and storage. Mirrors backend/answerLanguage.js:
 * the server re-validates every code, so this list only decides what is offered.
 *
 * Remembered PER USER, across sessions, on this device: localStorage keyed by
 * the signed-in member's `sub`, or "anon" when auth is off. Two members sharing
 * a browser keep their own choice; one member on two devices chooses once per
 * device (nothing is stored server-side in this pass).
 */
export const ANSWER_LANGUAGES = [
  // Auto: Gia replies in the language the question was typed in, which is how
  // it behaved before the picker. The default for everyone with no choice
  // stored. Its label is interface text, so it is English like the rest.
  { code: 'auto', native: 'Auto (match my question)', short: 'Auto' },
  { code: 'en', native: 'English', short: 'EN' },
  { code: 'es', native: 'Español', short: 'ES' },
  { code: 'pt', native: 'Português', short: 'PT' },
  { code: 'vi', native: 'Tiếng Việt', short: 'VI' },
  { code: 'id', native: 'Bahasa Indonesia', short: 'ID' },
  { code: 'zh', native: '中文', short: 'ZH' },
  { code: 'th', native: 'ไทย', short: 'TH' },
  { code: 'tr', native: 'Türkçe', short: 'TR' },
];

export const DEFAULT_ANSWER_LANGUAGE = 'auto';

export function isKnownLanguage(code) {
  return ANSWER_LANGUAGES.some((l) => l.code === code);
}

export function languageInfo(code) {
  return ANSWER_LANGUAGES.find((l) => l.code === code) || ANSWER_LANGUAGES[0];
}

export function languageStorageKey(user) {
  const sub = user && user.sub != null && String(user.sub).trim() ? String(user.sub).trim() : 'anon';
  return 'aqua-iq-answer-lang:' + sub;
}

export function loadAnswerLanguage(user) {
  try {
    const v = localStorage.getItem(languageStorageKey(user));
    return isKnownLanguage(v) ? v : DEFAULT_ANSWER_LANGUAGE;
  } catch { return DEFAULT_ANSWER_LANGUAGE; }
}

export function saveAnswerLanguage(user, code) {
  try { localStorage.setItem(languageStorageKey(user), isKnownLanguage(code) ? code : DEFAULT_ANSWER_LANGUAGE); } catch { /* quota / private mode */ }
}

/*
 * THE GUEST/ACCOUNT RACE. /auth/me answers after the first render, so the
 * signed-in member is unknown for a moment. The app starts from the language
 * of the LAST account seen on this device (so a returning member sees their
 * own choice at once, with no flash of another language), and a choice made
 * before /auth/me answers is held and written under the right key when it
 * does, instead of being saved under "anon" and then overwritten.
 */
const LAST_ACCOUNT_KEY = 'aqua-iq-answer-lang:last-account';

export function rememberLanguageAccount(user) {
  try { localStorage.setItem(LAST_ACCOUNT_KEY, user && user.sub != null && String(user.sub).trim() ? String(user.sub).trim() : 'anon'); } catch { /* private mode */ }
}

export function loadInitialAnswerLanguage() {
  let sub = null;
  try { sub = localStorage.getItem(LAST_ACCOUNT_KEY); } catch { /* private mode */ }
  return loadAnswerLanguage(sub && sub !== 'anon' ? { sub } : null);
}
